import { makeAutoObservable, runInAction, toJS } from 'mobx';
import {
	ASSET_DETILED,
	DECARBONIZATION_TARGET_INIT,
	ENERGY_ASSET_PROFILE_INIT,
	GHG_EMISSION_ACTIVITIES_INIT,
} from '../constants/dataCollection.constant';
import { dataCollectionBaseURL, onboardAssetURL } from '../api/url';
import { handleFetch } from '../../../utils/handleFetch';
import { showToast } from '../../../utils/toast';

class DataCollectionOverview {
	ghgEmissionActivities = GHG_EMISSION_ACTIVITIES_INIT;
	decarbonizationTarget = DECARBONIZATION_TARGET_INIT;
	energyAssetProfile = ENERGY_ASSET_PROFILE_INIT;
	assetList = [];
	energySystemTabs = [];
	selectedAssetId = null;
	selectedAssetName = '';
	isDeleteConfirmModalOpen = false;
	errors = {};
	decarbonizationError = {};

	isEdited = false;

	constructor() {
		makeAutoObservable(this);
	}

	handleCancel = async () => {
		await this.getDataCollectionOverview();
		runInAction(() => (this.isEdited = false));
	};

	getDataCollectionOverview = async () => {
		return await handleFetch({
			url: dataCollectionBaseURL,
			method: 'GET',
			onSuccess: (data) =>
				runInAction(() => {
					if (data.ghgEmissionActivities) {
						this.ghgEmissionActivities = data.ghgEmissionActivities;
					}

					if (data.decarbonizationTarget) {
						this.decarbonizationTarget = data.decarbonizationTarget;
						if (data.decarbonizationTarget.other) {
							this.otherSpecify = data.otherSpecifiy;
						}
					}
				}),
		});
	};

	saveDataCollectionOverview = async () => {
		const body = {
			ghgEmissionActivities: this.ghgEmissionActivities,
			decarbonizationTarget: this.decarbonizationTarget,
			otherSpecifiy: this.otherSpecify,
		};

		return showToast.promise(
			(async () => {
				const success = await handleFetch({
					url: dataCollectionBaseURL,
					method: 'PUT',
					body,
					onSuccess: () => {
						runInAction(() => {
							this.isEdited = false;
						});
					},
					onValidationError: (errors) => runInAction(() => (this.decarbonizationError = errors)),
				});
				if (!success) throw new Error('Failed to save data collection overview');
				return success;
			})(),
			{
				loading: 'Saving data collection overview...',
				success: 'Data collection overview saved successfully!',
				error: 'Failed to save data collection overview',
			},
		);
	};

	handleChangeDataCollection = (e, index) => {
		const { id, value } = e.target;
		if (index !== undefined) {
			this.ghgEmissionActivities['scope'][index][id] = value;
		} else {
			this.ghgEmissionActivities[id] = value;
		}
		this.isEdited = true;
	};

	handleSelectDecarbonization = (key, value) => {
		this.isEdited = true;
		this.decarbonizationTarget[key] = value;
	};

	handleChange = (e, i) => {
		const { id, value } = e?.target;
		if (i !== undefined) {
			this.energyAssetProfile['assetDetailed'][i][id] = value;
		} else {
			this.energyAssetProfile[id] = value;
		}
	};

	setAssetSpecification = (key, value, i) => {
		const assetDetail = this.energyAssetProfile.assetDetailed[i];

		if (!assetDetail.assetSpecifications) assetDetail.assetSpecifications = {};

		assetDetail.assetSpecifications[key] = value;
	};

	handleAddNewAssetDetail = () => {
		const newDetail = this.energyAssetProfile['assetDetailed'];
		newDetail.push({});
	};

	saveEnergyAssets = async () => {
		const { _id, ...body } = this.energyAssetProfile;
		const url = _id ? onboardAssetURL(_id) : onboardAssetURL();
		const isUpdate = !!_id;

		return showToast.promise(
			(async () => {
				const success = await handleFetch({
					url,
					method: isUpdate ? 'PUT' : 'POST',
					body,
					onSuccess: () => {
						runInAction(() => {
							this.getAllAssets();
							this.setInitForm();
							this.errors = {};
						});
					},
					onValidationError: (errors) => runInAction(() => (this.errors = errors)),
				});
				if (!success) throw new Error('Failed to save energy asset');
				return success;
			})(),
			{
				loading: isUpdate ? 'Updating energy asset...' : 'Creating energy asset...',
				success: isUpdate ? 'Energy asset updated successfully!' : 'Energy asset created successfully!',
				error: 'Failed to save energy asset',
			},
		);
	};

	setInitForm = () => {
		this.energyAssetProfile = ENERGY_ASSET_PROFILE_INIT;
	};

	getAllAssets = async () => {
		return await handleFetch({
			url: onboardAssetURL(),
			method: 'GET',
			onSuccess: (data) =>
				runInAction(() => {
					this.energySystemTabs = Object.keys(data).map((d) => {
						const [firstAsset] = data[d];
						return { key: d, value: firstAsset?.energySystemValue };
					});
					this.assetList = data;
				}),
		});
	};

	setAssetData = (id, activeTab) => {
		const asset = this.assetList?.[activeTab]?.find((a) => a._id === id);
		console.log('asset', asset, activeTab);
		if (asset) {
			this.energyAssetProfile._id = asset._id;
			this.energyAssetProfile.energySystem = asset.energySystem;
			this.energyAssetProfile.assetDetailed = [
				{
					assetName: asset.assetName,
					assetType: asset.assetType,
					assetSpecifications: asset.assetSpecifications,
					otherName: asset.otherName,
				},
			];
		} else {
			this.setInitForm();
		}
	};

	openDeleteConfirmModal = (assetId, assetName) => {
		this.selectedAssetId = assetId;
		this.selectedAssetName = assetName;
		this.isDeleteConfirmModalOpen = true;
	};

	closeDeleteConfirmModal = () => {
		this.isDeleteConfirmModalOpen = false;
	};

	deleteSelectedAsset = async (id) => {
		if (!id) return showToast.error('Something went wrong');
		return showToast.promise(
			(async () => {
				const success = await handleFetch({
					url: onboardAssetURL(id),
					method: 'DELETE',
					onSuccess: () => {
						this.closeDeleteConfirmModal();
						this.getAllAssets();
					},
				});
				if (!success) throw new Error('Failed to delete energy asset');
				return success;
			})(),
			{
				loading: 'Deleting energy asset...',
				success: 'Energy asset deleted successfully!',
				error: 'Failed to delete energy asset',
			},
		);
	};
}

export const dataCollectionStore = new DataCollectionOverview();
